import { resolve } from "inversify-react";
import * as moment from "moment";
import * as React from "react";
import { connect } from "react-redux";
import Modal from "src/core/ui/modal";
import { actions as homeActions } from "../../actions/home.actions";
import { actions } from "../../actions/main.actions";
import { actions as nameActions } from "../../actions/name.actions";
import Button from "../../core/ui/button";
import CountrySelect from "../../core/ui/data-component/country-select";
import PhoneInput from "../../core/ui/data-component/phone-input";
import DatePicker from "../../core/ui/datepicker";
import Form from "../../core/ui/form";
import Grid, { Col } from "../../core/ui/grid";
import Icon from "../../core/ui/icon";
import FormInput from "../../core/ui/input";
import Panel from "../../core/ui/panel";
import Tooltip from "../../core/ui/tooltip";
import Upload from "../../core/ui/upload";
import BlockchainService from "../../services/blockchain.service";
import IdentityService from "../../services/identity.service";
import PrivateFileSystem from "../../services/private.filesystem.service";
import ToastService from "../../services/toast.service";
import AddCreditsComponent from "./parts/add-credits-component";
import { UserService } from "../../services/openapi/generated";
import { IApplicationState } from "../../typings";

interface IAccountBalanceProps {
    userEmail: string;
    userPhone: string;
    name: string;
    userDateOfBirth: string;
    userCitizenship: string;
    prove_of_address?: string;
    prove_of_identity?: string;
    wallet_address: string;
    address: string;
    city: string;
    postCode: string;
    last_name: string;
    country: string;
}

type AccountBalanceProps = IAccountBalanceProps & typeof actions & typeof homeActions;
interface IAccountBalanceState {
    email: string;
    phone: string;

    name: string;
    last_name: string;
    date_of_birth: string;
    citizenship: string;
    wallet_address: string;
    address: string;
    city: string;
    postCode: string;
    country: string;

    prove_of_identity?: string;
    prove_of_address?: string;

    nameErrors?: string[];
    emailErrors?: string[];
    phoneErrors?: string[];
    lastNameErrors?: string[];
    dateOfBirthErrors?: string[];
    citizenshipErrors?: string[];
    generalErrors?: string[];
    walletErrors?: string[];
    postCodeErrors?: string[];
    cityErrors?: string[];
    addressErrors?: string[];

    last_nameErrors?: string[];
    countryErrors?: string[];

    addCreditsModal: boolean;
    processingPayment: boolean;
}

class AccountBalance extends React.Component<AccountBalanceProps, IAccountBalanceState> {
    @resolve(IdentityService)
    public identity: IdentityService;

    @resolve(PrivateFileSystem)
    public fileSystemService: PrivateFileSystem;

    @resolve
    public blockChainService: BlockchainService;

    @resolve
    public toastService: ToastService;

    constructor(props: any) {
        super(props);
        const now = moment(Date.now());
        this.state = {
            address: this.props.address,
            citizenship: this.props.userCitizenship,
            city: this.props.city,
            country: this.props.country,
            date_of_birth: !!this.props.userDateOfBirth ? this.props.userDateOfBirth : now.format("YYYY-MM-DD"),
            email: this.props.userEmail,
            last_name: this.props.last_name,
            name: this.props.name,
            phone: this.props.userPhone,
            postCode: this.props.postCode,
            prove_of_address: this.props.prove_of_address,
            prove_of_identity: this.props.prove_of_identity,
            wallet_address: this.props.wallet_address,
            addCreditsModal: false,
            processingPayment: false,
        };
    }

    public componentWillReceiveProps(nextProps: AccountBalanceProps) {
        if (nextProps.userEmail !== this.props.userEmail) {
            this.setState({ email: nextProps.userEmail });
        }
        if (nextProps.userPhone !== this.props.userPhone) {
            this.setState({ phone: nextProps.userPhone });
        }
        if (nextProps.userCitizenship !== this.props.userCitizenship) {
            this.setState({ citizenship: nextProps.userCitizenship });
        }
        if (nextProps.userDateOfBirth !== this.props.userDateOfBirth) {
            this.setState({ date_of_birth: nextProps.userDateOfBirth });
        }
        if (nextProps.name !== this.props.name) {
            this.setState({ name: nextProps.name });
        }
        if (nextProps.last_name !== this.props.last_name) {
            this.setState({ last_name: nextProps.last_name });
        }
        if (nextProps.prove_of_address !== this.props.prove_of_address) {
            this.setState({ prove_of_address: nextProps.prove_of_address });
        }
        if (nextProps.prove_of_identity !== this.props.prove_of_identity) {
            this.setState({ prove_of_identity: nextProps.prove_of_identity });
        }
        if (nextProps.address !== this.props.address) {
            this.setState({ address: nextProps.address });
        }
        if (nextProps.city !== this.props.city) {
            this.setState({ city: nextProps.city });
        }
        if (nextProps.country !== this.props.country) {
            this.setState({ country: nextProps.country });
        }
        if (nextProps.postCode !== this.props.postCode) {
            this.setState({ postCode: nextProps.postCode });
        }
        if (nextProps.wallet_address !== this.props.wallet_address) {
            this.setState({ wallet_address: nextProps.wallet_address });
        }
    }

    public componentDidMount() {
        this.identity.refreshUserInfo();
        this.props.changeLocation("balance");
        this.setState({
            email: this.props.userEmail,
            phone: this.props.userPhone,
        });
    }

    public verify = () => {
        if (!this.identity.isVerified) {
            UserService.userControllerVerifyEmail(this.identity.getToken())
                .then((result) => {
                    this.toastService.showToast(
                        "User verification",
                        "We send a verification request to the gallery owner, wait for the verification feedback",
                        "success",
                    );
                })
                .catch((error) => {
                    this.toastService.showToast("Error", "Please check errors below", "error");
                });
        }
    }

    public updateIdentity = () => {
        this.identity
            .update(
                {
                    citizenship: this.state.citizenship,
                    date_of_birth: this.state.date_of_birth,
                    last_name: this.state.last_name,
                    name: this.state.name,
                    prove_of_identity: this.state.prove_of_identity,
                },
                false,
            )
            .then((result) => {
                this.toastService.showToast("User identity data update", "The user data had been updated correctlly", "success");
            })
            .catch((error) => {
                this.toastService.showToast("Error", "Please check errors below", "error");
                this.setState({
                    citizenshipErrors: error.citizenship,
                    countryErrors: error.country,
                    dateOfBirthErrors: error.date_of_birth,
                    nameErrors: error.full_name,
                });
            });
    };

    public updateAddress = (e) => {
        this.identity
            .update(
                {
                    address: this.state.address,
                    city: this.state.city,
                    country: this.state.country,
                    postCode: this.state.postCode,
                    prove_of_address: this.state.prove_of_address,
                },
                false,
            )
            .then((result) => {
                this.toastService.showToast("User address data update", "The user data had been updated correctlly", "success");
            })
            .catch((error) => {
                this.toastService.showToast("Error", "Please check errors below", "error");
                this.setState({
                    addressErrors: error.address,
                    cityErrors: error.city,
                    postCodeErrors: error.postCode,
                });
            });
    }

    public updateContact = () => {
        this.identity
            .update(
                {
                    email: this.state.email,
                    phone: this.state.phone,
                    wallet_address: this.state.wallet_address,
                },
                true,
            )
            .then((result) => {
                this.toastService.showToast("User contact data update", "The user data had been updated correctlly", "success");
            })
            .catch((error) => {
                this.toastService.showToast("Error", "Please check errors below", "error");
                this.setState({
                    emailErrors: error.email,
                    phoneErrors: error.phone,
                    walletErrors: error.wallet_address,
                });
            });
    }

    public render() {
        const headerSize = 38;
        const now = moment(Date.now());
        return (
            <React.Fragment>
                <Modal
                    className="payment-modal"
                    closable={!this.state.processingPayment}
                    size="small"
                    visible={this.state.addCreditsModal}
                    onCancel={() => this.setState({ addCreditsModal: false })}
                >
                    <AddCreditsComponent
                        onProcessing={() => this.setState({ processingPayment: true })}
                        onSuccess={() => this.setState({ processingPayment: false })}
                        onError={() => this.setState({ processingPayment: false })}
                    />
                </Modal>
                <Form
                    onSubmit={(e) => {
                        e.prenventDefault();
                    }}
                >
                    <Grid centered={true}>
                        <Col size={80} style={{ display: "flex", justifyContent: "space-between" }}>
                            {this.identity.isArtist && (
                                <div className="credits-container">
                                    <p>Credits: {this.identity.userInfo ? this.identity.userInfo.credits : 0}</p>
                                    <Button
                                        className="add-credits-button"
                                        icon="plus"
                                        onClick={() => this.setState({ addCreditsModal: true })}
                                    />
                                </div>
                            )}
                            {!this.identity.isVerified && (
                                <Tooltip
                                    className="account-profile-tooltip"
                                    trigger={
                                        <div className="tooltip1 pink">
                                            <Icon type="help" />
                                            <p>Account not verified</p>
                                        </div>
                                    }
                                    content={
                                        <p>
                                            Complete the Account page and after confirming that the information you provided is correct, you
                                            will have access to the full platform.
                                        </p>
                                    }
                                />
                            )}
                            {this.identity.isVerified && <p className="verified-label">ACCOUNT VERIFIED</p>}
                        </Col>
                    </Grid>
                    <Grid centered={true}>
                        <Col size={85}>
                            <Panel>
                                <Grid centered={true}>
                                    <Col className="panel-title" size={headerSize}>
                                        <div className="panel-title-header">
                                            <h1 className="required"></h1>
                                            <h1 className="panel-header">Contact Details</h1>
                                        </div>
                                    </Col>
                                    <Col className="flex-column" size={100 - headerSize}>
                                        {/*<FormInput
                                            style='dark'
                                            label='Mobile Phone'
                                            errors={this.state.phoneErrors}
                                            value={this.props.userPhone}
                                            onChange={e => this.setState({phone: e})}
                                        />*/}
                                        <PhoneInput
                                            label="Mobile Phone"
                                            value={this.props.userPhone}
                                            onChange={(e) => this.setState({ phone: e })}
                                        />
                                        <FormInput
                                            style="dark"
                                            label="E-mail"
                                            disabled={true}
                                            errors={this.state.emailErrors}
                                            value={this.props.userEmail}
                                            onChange={(e) => this.setState({ email: e })}
                                        />
                                        <FormInput
                                            style="dark"
                                            label="Wallet address"
                                            disabled={true}
                                            errors={this.state.walletErrors}
                                            value={this.props.wallet_address}
                                            onChange={(e) => this.setState({ wallet_address: e })}
                                            extraContent={[
                                                <Button
                                                    onClick={this.updateContact}
                                                    as={<button />}
                                                    extraProps={{ type: "submit" }}
                                                    presentation="dark"
                                                    className="huge-btn"
                                                >
                                                    UPDATE
                                                </Button>,
                                            ]}
                                        />
                                    </Col>
                                </Grid>
                            </Panel>
                            <Panel>
                                <Grid centered={true}>
                                    <Col className="panel-title" size={headerSize}>
                                        <div className="panel-title-header">
                                            <h1 className="required"></h1>
                                            <h1 className="panel-header">Personal Information</h1>
                                        </div>
                                    </Col>
                                    <Col size={100 - headerSize}>
                                        <FormInput
                                            style="dark"
                                            label="Name"
                                            errors={this.state.nameErrors}
                                            value={this.props.name}
                                            onChange={(e) => this.setState({ name: e })}
                                        />
                                        <FormInput
                                            style="dark"
                                            label="Last Name"
                                            errors={this.state.nameErrors}
                                            value={this.props.last_name}
                                            onChange={(e) => this.setState({ last_name: e })}
                                        />
                                        <DatePicker
                                            label="Date of birth"
                                            format="YYYY-MM-DD"
                                            errors={this.state.dateOfBirthErrors}
                                            value={this.state.date_of_birth}
                                            onChange={(e) => this.setState({ date_of_birth: e })}
                                        />
                                        <CountrySelect
                                            value={this.state.citizenship}
                                            onChange={(e) => this.setState({ citizenship: e.code })}
                                            label="Citizenship"
                                            placeholder="Select a country"
                                        />
                                    </Col>
                                </Grid>
                                <Grid centered={true}>
                                    <Col size={headerSize}>
                                        <div className="aclaration">
                                            <h2>
                                                <strong>Proof</strong> of identity
                                            </h2>
                                            <p>
                                                For verification, we require at least two different proof of identity documents.
                                                <br />
                                                Please upload a valid photo ID, and with the visible date of issue. Please also provide your
                                                date of birth and your citizenship.
                                            </p>
                                        </div>
                                    </Col>
                                    <Col size={100 - headerSize}>
                                        <Grid>
                                            <Col size={1} />
                                            <Col size={2}>
                                                <Upload
                                                    proof={true}
                                                    fileSystem={this.fileSystemService}
                                                    value={this.state.prove_of_identity}
                                                    onUpload={(e) => {
                                                        this.setState({ prove_of_identity: e.id });
                                                        console.log("e: " + e);
                                                        console.log("e[0]: " + e[0]);
                                                        console.log("id: " + e.id);
                                                        console.log("identity_photo: " + this.state.prove_of_identity);
                                                    }}
                                                    onDelete={() => this.setState({ prove_of_identity: undefined })}
                                                />
                                                <Button
                                                    onClick={this.updateIdentity}
                                                    as={<button />}
                                                    presentation="light"
                                                    className="button huge-btn"
                                                >
                                                    Update
                                                </Button>
                                            </Col>
                                        </Grid>
                                    </Col>
                                </Grid>
                            </Panel>
                            <Panel>
                                <Grid centered={true}>
                                    <Col className="panel-title" size={headerSize}>
                                        <div className="panel-title-header">
                                            <h1 className="required"></h1>
                                            <h1 className="panel-header">Residential Address</h1>
                                        </div>
                                    </Col>
                                    <Col size={100 - headerSize}>
                                        <FormInput
                                            style="dark"
                                            label="Address"
                                            errors={this.state.addressErrors}
                                            value={this.props.address}
                                            onChange={(e) => this.setState({ address: e })}
                                        />
                                        <FormInput
                                            style="dark"
                                            label="City or town"
                                            value={this.props.city}
                                            errors={this.state.cityErrors}
                                            onChange={(e) => this.setState({ city: e })}
                                        />
                                        <FormInput
                                            style="dark"
                                            label="Post code"
                                            value={this.props.postCode}
                                            errors={this.state.postCodeErrors}
                                            onChange={(e) => this.setState({ postCode: e })}
                                        />
                                        <CountrySelect
                                            label="Country"
                                            placeholder="Select a country"
                                            value={this.state.country}
                                            onChange={(e) => this.setState({ country: e.code })}
                                        />
                                    </Col>
                                </Grid>
                                <Grid centered={true}>
                                    <Col size={headerSize}>
                                        <div className="aclaration">
                                            <h2>
                                                <strong>Proof</strong> of address
                                            </h2>
                                            <p>
                                                For verification, we require at least two different proof of identity documents.
                                                <br />
                                                Please upload a Bank statement, tenancy agreement, utility bill or tax filing with name
                                                shown.
                                            </p>
                                        </div>
                                    </Col>
                                    <Col size={100 - headerSize}>
                                        <Grid>
                                            <Col size={1} />
                                            <Col size={2}>
                                                <Upload
                                                    proof={true}
                                                    fileSystem={this.fileSystemService}
                                                    value={this.state.prove_of_address}
                                                    onUpload={(e) => this.setState({ prove_of_address: e.id })}
                                                    onDelete={() => this.setState({ prove_of_address: undefined })}
                                                />
                                                <Button
                                                    onClick={this.updateAddress}
                                                    as={<button />}
                                                    presentation="light"
                                                    className="button huge-btn"
                                                >
                                                    Update
                                                </Button>
                                            </Col>
                                        </Grid>
                                    </Col>
                                </Grid>
                            </Panel>
                        </Col>
                    </Grid>
                </Form>
            </React.Fragment>
        );
    }
}

function mapStateToProps(state: IApplicationState, ownProps: AccountBalanceProps): AccountBalanceProps {
    return {
        ...ownProps,
        address: state.identity.userInfo ? state.identity.userInfo.address : "",
        city: state.identity.userInfo ? state.identity.userInfo.city : "",
        country: state.identity.userInfo ? state.identity.userInfo.country : "ES",
        last_name: state.identity.userInfo ? state.identity.userInfo.last_name : "",
        name: state.identity.userInfo ? state.identity.userInfo.name : "",
        postCode: state.identity.userInfo ? state.identity.userInfo.postCode : "",
        prove_of_address: state.identity.userInfo ? state.identity.userInfo.prove_of_address : undefined,
        prove_of_identity: state.identity.userInfo ? state.identity.userInfo.prove_of_identity : undefined,
        userCitizenship: state.identity.userInfo ? state.identity.userInfo.citizenship : "ES",
        userDateOfBirth: state.identity.userInfo ? state.identity.userInfo.date_of_birth : new Date(Date.now()).toString(),
        userEmail: state.identity.userInfo ? state.identity.userInfo.email : "",
        userPhone: state.identity.userInfo ? state.identity.userInfo.phone : "",
        wallet_address: state.identity.userInfo ? state.identity.userInfo.wallet_address : "",
    };
}
export default connect(mapStateToProps, { ...actions, ...nameActions, ...homeActions })(AccountBalance);



// WEBPACK FOOTER //
// ./src/components/account/account-profile.tsx